import { User } from './../../../../interfaces/interface-bakend/seguridad/users/user.interface';
import { Component, Input, OnInit } from '@angular/core';
import { Solicitud } from '../../../../interfaces/interface-bakend/ofertas-laborales/solicitudes.interface';
import { UserEntreUser } from '@interfaces/interface-bakend/seguridad/users/user-entre-user.interface';

@Component({
  selector: 'app-ofertas-estado-detalle',
  templateUrl: './ofertas-estado-detalle.component.html',
  styleUrls: ['./ofertas-estado-detalle.component.scss']
})
export class OfertasEstadoDetalleComponent implements OnInit {

  @Input() solicitud: Solicitud;
  @Input() usuario: User;
  @Input() beneficiario: UserEntreUser;
  @Input() nombrePais: string;
  @Input() estadoFormulario: string;
  @Input() estadoDocumentacion: string;
  @Input() estadoPagoTotal: string;
  @Input() cobertura?: string;
  @Input() esSedeInscripcion: boolean = false;  // <-- NUEVO INPUT
  /** Qué card renderizar: ambas (default), solo contacto o solo detalles. */
  @Input() seccion: 'ambas' | 'contacto' | 'detalles' = 'ambas';

  constructor() { }

  ngOnInit(): void { }

  get fechaNacimientoFormateada(): string {
    // Si es inscripción, usar los datos del formulario de inscripción
    if (this.esSedeInscripcion && this.solicitud.inscripcion?.contenido) {
      const titular = (this.solicitud.inscripcion.contenido as any).datosDelTitular;
      if (titular?.fechaNacimiento) {
        // Convertir ISO a formato dd/MM/yyyy
        return new Date(titular.fechaNacimiento).toLocaleDateString('es-AR');
      }
    }
    // Comportamiento original (para solicitudes que no son de inscripción)
    return this.fechaNacimientoDe(this.usuario);
  }

  get edad(): number {
    // Si es inscripción, el dato vive en el formulario del titular.
    if (this.esSedeInscripcion && this.solicitud.inscripcion?.contenido) {
      const titular = (this.solicitud.inscripcion.contenido as any).datosDelTitular;
      if (titular?.fechaNacimiento) {
        return this.calcularEdad(new Date(titular.fechaNacimiento));
      }
    }
    return this.edadDe(this.usuario);
  }

  /**
   * Teléfono de contacto. En inscripción, la proto-cuenta no guarda el teléfono
   * en `usuario`; el dato vive en la solicitud (datosDelTitular).
   */
  get telefonoContacto(): string {
    if (this.esSedeInscripcion && this.solicitud?.inscripcion?.contenido) {
      const titular = (this.solicitud.inscripcion.contenido as any).datosDelTitular;
      if (titular?.telefono) {
        return `${titular.telefono}`;
      }
    }
    return this.telefonoDe(this.usuario);
  }

  get celularContacto(): string {
    if (this.esSedeInscripcion && this.solicitud?.inscripcion?.contenido) {
      const titular = (this.solicitud.inscripcion.contenido as any).datosDelTitular;
      if (titular?.celular) {
        return `${titular.celular}`;
      }
    }
    return this.celularDe(this.usuario);
  }

  // ---------- Datos de contacto del beneficiario ----------

  /** Usuario del beneficiario cuando el trámite es para un familiar. */
  get beneficiarioUser(): User {
    return this.beneficiario?.adicional || this.solicitud?.beneficiario || null;
  }

  /**
   * La card del beneficiario sólo se muestra si es un afiliado distinto del
   * titular que hizo la solicitud (evita duplicar la card del titular cuando
   * el trámite es del propio titular).
   */
  get mostrarContactoBeneficiario(): boolean {
    const beneficiario = this.beneficiarioUser;
    return !!beneficiario?._id && beneficiario._id !== this.usuario?._id;
  }

  /** true si el beneficiario de la solicitud es el propio titular. */
  get esTitularYBeneficiario(): boolean {
    const beneficiario = this.beneficiarioUser;
    return !!beneficiario?._id && beneficiario._id === this.usuario?._id;
  }

  /** Título de la card de contacto del titular (agrega "Beneficiario" si coincide). */
  get tituloContactoTitular(): string {
    return this.esTitularYBeneficiario
      ? 'Datos de contacto — Titular y Beneficiario'
      : 'Datos de contacto — Titular';
  }

  get beneficiarioFechaNacimiento(): string {
    return this.fechaNacimientoDe(this.beneficiarioUser);
  }

  get beneficiarioEdad(): number {
    return this.edadDe(this.beneficiarioUser);
  }

  get beneficiarioTelefono(): string {
    return this.telefonoDe(this.beneficiarioUser);
  }

  get beneficiarioCelular(): string {
    return this.celularDe(this.beneficiarioUser);
  }

  // ---------- Helpers por persona ----------

  private fechaNacimientoDe(afiliado: User): string {
    return afiliado?.signoGecrosAfiliado?.fechaNacimiento || '';
  }

  /** Edad a partir de la fecha de nacimiento (formato dd/MM/yyyy). */
  private edadDe(afiliado: User): number {
    const fechaStr = afiliado?.signoGecrosAfiliado?.fechaNacimiento;
    if (!fechaStr) return 0;

    const partes = fechaStr.split('/');
    if (partes.length !== 3) return 0;

    const fechaNacimiento = new Date(
      parseInt(partes[2]),      // año
      parseInt(partes[1]) - 1,  // mes
      parseInt(partes[0])       // día
    );
    return this.calcularEdad(fechaNacimiento);
  }

  private calcularEdad(fechaNacimiento: Date): number {
    if (!fechaNacimiento || isNaN(fechaNacimiento.getTime())) return 0;

    const hoy = new Date();
    let edad = hoy.getFullYear() - fechaNacimiento.getFullYear();
    const mes = hoy.getMonth() - fechaNacimiento.getMonth();

    if (mes < 0 || (mes === 0 && hoy.getDate() < fechaNacimiento.getDate())) {
      edad--;
    }

    return edad;
  }

  private telefonoDe(afiliado: User): string {
    return `${afiliado?.paisCodeArea || ''}${afiliado?.telefono || ''}`;
  }

  private celularDe(afiliado: User): string {
    return (afiliado as any)?.celular || '-';
  }
}